import { useEffect, useState, useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Container, Col, Row } from 'react-bootstrap';
import { CustomCheckbox } from '../../../components/common';
import axios from 'axios';
import PropTypes from 'prop-types';

import { Avatar, SiteSpinner, TextBlock } from '../../../components/common';
import { constants, Utils } from '../../../helpers';

import { usePoliciesStore } from '../../../store/policiesStore';
import { useCustomerOrgUsers } from '../../../store/CustomerOrgUsersStore';
import { RxMagnifyingGlass } from 'react-icons/rx';
import './ContributorsTab.scss';

const ContributorsTab = ({
  orgDetails,
  selectedIncident,
  incidentContributors,
  setIncidentContributors,
  setShowToast,
  setUserMsg,
  statusClosed,
  setShowPendingChangesModal,
  ...props
}) => {
  const {
    register,
    formState: { errors },
  } = useForm();

  const [showLoading, setShowLoading] = useState(false);
  const [hasData, setHasData] = useState(false);
  const [organizationUsers, setOrganizationUsers] = useState([]);
  const [contributorsCache, setContributorsCache] = useState({});
  const [searchText, setSearchText] = useState('');

  const { getLoggedInUserPolicies } = usePoliciesStore();
  const getCustomerOrgUsers = useCustomerOrgUsers(
    (state) => state.getCustomerOrgUsers,
  );
  const getCustomerOrgUsersData = useCustomerOrgUsers(
    (state) => state.getCustomerOrgUsersData,
  );

  const userPolicies = getLoggedInUserPolicies();

  const customerOrgUsers = getCustomerOrgUsersData();

  useEffect(() => {
    fetchData();
  }, []);

  const filteredUsers = useMemo(() => {
    if (!searchText) return organizationUsers;
    return organizationUsers.filter((user) =>
      user?.name?.toLowerCase().includes(searchText.toLowerCase()),
    );
  }, [organizationUsers, searchText]);

  const fetchContributors = useCallback(async () => {
    if (!orgDetails || !selectedIncident?.incidentId) {
      return;
    }

    // Check cache first
    const cacheKey = `${orgDetails.orgId}-${selectedIncident.incidentId}`;
    if (contributorsCache[cacheKey]) {
      return contributorsCache[cacheKey];
    }

    try {
      const res = await axios.get(
        `/incident/orgs/${orgDetails?.orgId}/incidents/${selectedIncident?.incidentId}/users`,
        {
          ...Utils.requestHeader(),
          credentials: 'include',
          withCredentials: true,
        },
      );

      const responseData = res?.data;

      if (responseData?.meta?.code === 200) {
        const users = responseData?.data?.users;
        // Update cache
        setContributorsCache((prev) => ({
          ...prev,
          [cacheKey]: users,
        }));
        return users;
      } else {
        if (res?.code) {
          Utils.vmsLogger().error(`${res.code}: ${res.message}`);
        } else if (responseData?.data) {
          Utils.vmsLogger().error(responseData?.data?.userMsg);
        }
      }
    } catch (error) {
      Utils.vmsLogger().error(error);
      return [];
    }
  }, [orgDetails, selectedIncident, contributorsCache]);

  const fetchData = async () => {
    try {
      setShowLoading(true);

      const getResult = await getCustomerOrgUsers(
        `partner/orgs/${orgDetails?.orgId}/accounts`,
      );

      if (getResult?.status === 'success') {
        setOrganizationUsers(structuredClone(getCustomerOrgUsersData()));
      } else {
        setOrganizationUsers(structuredClone(customerOrgUsers));
      }

      const contributorsData = (await fetchContributors()) ?? [];
      if (!contributorsData) {
        return;
      }

      const contributorsWithAvatarAndInitials = contributorsData?.map(
        (contributor) => {
          const accountData = customerOrgUsers.find(
            (orgUser) => orgUser?.accountId === contributor?.accountId,
          );

          return {
            ...contributor,
            ...accountData,
          };
        },
      );

      setIncidentContributors &&
        setIncidentContributors([...contributorsWithAvatarAndInitials]);
      setHasData(true);
    } catch (error) {
      Utils.vmsLogger().error(error);
      setHasData(false);
    } finally {
      setShowLoading(false);
    }
  };

  const handleContributorsUpdate = async (contributors) => {
    if (!orgDetails || !selectedIncident?.incidentId) {
      return [];
    }

    try {
      if (!Array.isArray(contributors)) return;

      const contributorAccountIds = contributors.map(
        (contributor) => contributor.accountId,
      );

      const reqBody = {
        accountIds: [...contributorAccountIds],
      };

      const res =
        orgDetails?.orgId &&
        selectedIncident?.incidentId &&
        (await axios.put(
          `/incident/orgs/${orgDetails?.orgId}/incidents/${selectedIncident?.incidentId}/users`,
          reqBody,
          Utils.requestHeader(),
        ));
      if (!res) {
        Utils.vmsLogger().error(
          'axios.put() returned null or undefined for contributors update.',
        );
        return;
      }

      const responseData = res?.data;

      if (responseData?.meta?.code === 200) {
        setIncidentContributors([...responseData?.data?.users]);
      } else if (res?.code) {
        Utils.vmsLogger().error(`${res.code}: ${res.message}`);
      } else if (responseData?.data) {
        Utils.vmsLogger().error(responseData?.data?.userMsg);
      }
    } catch (error) {
      Utils.vmsLogger().error(error);
      return [];
    }
  };

  const handleCheckboxChange = useCallback(
    async (e) => {
      const targetValue = e.currentTarget.querySelector('input')?.value;
      try {
        const value = JSON.parse(targetValue);
        const currentContributors = incidentContributors || [];

        // Use Set for O(1) lookup instead of findIndex
        const contributorIds = new Set(
          currentContributors.map((c) => c.accountId),
        );
        const isContributor = contributorIds.has(value.accountId);

        // Create new array only if needed
        const updatedContributors = isContributor
          ? currentContributors.filter((c) => c.accountId !== value.accountId)
          : [...currentContributors, value];

        // Optimistic update
        setIncidentContributors(updatedContributors);

        // Update backend
        await handleContributorsUpdate(updatedContributors);
      } catch (error) {
        Utils.vmsLogger().error(error);
        setShowToast && setShowToast(true);
        setUserMsg && setUserMsg('Failed to update contributor selection');
      }
    },
    [
      incidentContributors,
      handleContributorsUpdate,
      setIncidentContributors,
      setShowToast,
      setUserMsg,
    ],
  );

  return (
    <Container className="contributors-content-area">
      {/* Contributors Title */}
      <Row>
        <Col>
          <div className="d-flex justify-content-start mt-4 mb-4 contributors-title-wrapper">
            <span className="contributors-title">
              {constants.INCIDENTS_PAGE_TAB_CONTRIBUTORS_LABEL}
            </span>
          </div>
        </Col>
      </Row>
      {/* Search Bar */}
      <Row>
        <Col>
          <RxMagnifyingGlass className="search-icon-contributors" />
          <input
            type="text"
            className="form-control mb-3"
            placeholder="Search by name"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            autoComplete="off"
          />
        </Col>
      </Row>
      {/* Table of Contributors */}
      <Row className="">
        <Col>
          {showLoading ? (
            <div className="mb-2 contributors-content">
              <SiteSpinner height="50px" width="50px" />
              <div className="mt-2 text-dark loader-color-incident">
                {constants.LOADING}
              </div>
            </div>
          ) : hasData ? (
            <div className="mb-2 contributors-content">
              {Array.isArray(filteredUsers) &&
                filteredUsers.map((orgUser, orgUserIndex) => (
                  <Row key={orgUser?.accountId}>
                    <Col>
                      <div className="mb-3 contributors-content-wrapper">
                        <TextBlock className="contributors-content-item">
                          <div className="contributors-content-item-details">
                            <div>
                              <Avatar
                                valueType={
                                  orgUser?.image?.url ? 'icon' : 'text'
                                }
                                value={
                                  orgUser?.image?.url
                                    ? orgUser?.image?.url
                                    : orgUser?.initials
                                }
                                size="medium"
                                avatarStyle="roundedCircle"
                              />
                            </div>
                            <div className="contributors-content-item-info">
                              <div className="view-log-detail-creator-name">
                                {orgUser?.name}
                              </div>
                            </div>
                          </div>
                          <CustomCheckbox
                            customClassName={'contributors-checkbox'}
                            value={JSON.stringify(orgUser)}
                            checked={incidentContributors?.some(
                              (contributor) =>
                                contributor.accountId === orgUser?.accountId,
                            )}
                            checkedColor="#F37321" // var(--surface-colored-brand-active)
                            uncheckedColor="var(--stroke-border-box)"
                            uncheckedFill="var(--surface-neutral-background)"
                            onChange={handleCheckboxChange}
                            disabled={
                              !userPolicies.update_all_incidents &&
                              !userPolicies.update_created_incident &&
                              !userPolicies.update_shared_incident
                            }
                          />
                        </TextBlock>
                      </div>
                    </Col>
                  </Row>
                ))}
            </div>
          ) : (
            <div className="mb-2 summary-input-wrapper">No Content</div>
          )}
        </Col>
      </Row>
    </Container>
  );
};

ContributorsTab.propTypes = {
  orgDetails: PropTypes.object,
  selectedIncident: PropTypes.shape({
    incidentId: PropTypes.string.isRequired,
  }),
  incidentContributors: PropTypes.array,
  setIncidentContributors: PropTypes.func,
  setShowToast: PropTypes.func,
  setUserMsg: PropTypes.func,
  statusClosed: PropTypes.bool,
  setShowPendingChangesModal: PropTypes.func,
};

export default ContributorsTab;
